import React, { useState, useEffect, useRef, useCallback } from "react";
import { ArrowLeftIcon, ArrowDownBlackIcon, ArrowDownWhiteIcon, PlusIcon, ArrowRightIcon, ClockIcon } from "~/assets/icons";
import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import PrimaryInput from "~/components/common/primary-input/PrimaryInput";
import { Dropdown } from "~/components/common/Dropdown";
import { notify } from "~/utils/helper";
import jobDetailsApi, { IBranch, IProduct, IProductPricingPayload, ICreateMaterialOrderPayload } from "~/apis/jobDetails";

interface OrderItem {
  id: string;
  itemName: string;
  // Product fields
  itemNumber: string;
  itemDescription?: string;
  familyName?: string | null;
  familyId?: string | null;
  isDimensional?: boolean;
  colorName?: string;
  category?: string;
  status?: string;
  variationUom?: string;
  variationUomCode?: string;
  uom: string;
  // Order fields
  qty: number;
  price: number;
  lineTotal: number;
}

interface Section {
  id: string;
  name: string;
  items: OrderItem[];
}

export interface MaterialOrder {
  id: string;
  materialOrderId: string;
  date: string;
  time: string;
  internalNote: string;
  total: string;
  status: "Draft" | "Pending" | "In Progress" | "Completed";
}

interface CreateMaterialOrderFormProps {
  onCancel: () => void;
  onSubmit: () => void; // Callback after successful submit
  previousOrders: MaterialOrder[];
  jobId: string;
  editOrderId?: string; // Optional: ID of order to edit
}

const CreateMaterialOrderForm: React.FC<CreateMaterialOrderFormProps> = ({
  onCancel,
  onSubmit,
  previousOrders,
  jobId,
  editOrderId,
}) => {
  const [selectedState, setSelectedState] = useState("");
  const [selectedBranch, setSelectedBranch] = useState("");
  const [materialOrderId, setMaterialOrderId] = useState("");
  const [orderDate, setOrderDate] = useState("");
  const [orderTime, setOrderTime] = useState("");
  const [selectedPreviousOrder, setSelectedPreviousOrder] = useState("");
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [showBranchDropdown, setShowBranchDropdown] = useState(false);
  const [branches, setBranches] = useState<IBranch[]>([]);
  const [isLoadingBranches, setIsLoadingBranches] = useState(false);
  const [productSuggestions, setProductSuggestions] = useState<{ [key: string]: IProduct[] }>({});
  const [showProductDropdown, setShowProductDropdown] = useState<{ [key: string]: boolean }>({});
  const [isLoadingProducts, setIsLoadingProducts] = useState<{ [key: string]: boolean }>({});
  const [isLoadingPricing, setIsLoadingPricing] = useState<{ [key: string]: boolean }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isCloning, setIsCloning] = useState(false);
  const [isLoadingOrderData, setIsLoadingOrderData] = useState(false);
  const [currentOrderStatus, setCurrentOrderStatus] = useState<string | null>(null);
  const productRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [streetAddress, setStreetAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [requestedDeliveryDate, setRequestedDeliveryDate] = useState("");
  const [notes, setNotes] = useState("");
  const [notesToSupplier, setNotesToSupplier] = useState("");
  const [deliveryService, setDeliveryService] = useState("");
  const [instructionsTypeCode, setInstructionsTypeCode] = useState("");
  const [fromTime, setFromTime] = useState("");
  const [toTime, setToTime] = useState("");
  const [sections, setSections] = useState<Section[]>([
    {
      id: "1",
      name: "Section 1",
      items: [
        { id: "1-1", itemName: "", itemNumber: "", uom: "Unit", qty: 0, price: 0, lineTotal: 0 },
        { id: "1-2", itemName: "", itemNumber: "", uom: "Unit", qty: 0, price: 0, lineTotal: 0 },
      ],
    },
  ]);
  const stateRef = useRef<HTMLDivElement>(null);
  const branchRef = useRef<HTMLDivElement>(null);

  // Validation errors state
  const [errors, setErrors] = useState<{
    selectedBranch?: string;
    materialOrderId?: string;
    orderDate?: string;
    orderTime?: string;
    streetAddress?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    deliveryService?: string;
    instructionsTypeCode?: string;
  }>({});

  const previousOrderOptions = previousOrders.map((order) => ({ value: order.materialOrderId, label: order.materialOrderId }));

  // Delivery Service Enum Options
  const deliveryServiceOptions = [
    { value: "COM", label: "Common Carrier" },
    { value: "CPU", label: "Customer Pickup" },
    { value: "EXP", label: "Express Pickup" },
    { value: "OTR", label: "Our Truck Roof" },
    { value: "OTG", label: "Our Truck Ground" },
    { value: "OTW", label: "Our Truck Window" },
    { value: "TPC", label: "Third Party Carrier" },
  ];

  // Instructions Type Code Enum Options
  const instructionsTypeCodeOptions = [
    { value: "AT", label: "Anytime Delivery" },
    { value: "AM", label: "Morning Delivery" },
    { value: "PM", label: "Afternoon Delivery" },
    { value: "FS", label: "First Stop Delivery" },
    { value: "ST", label: "Specific Time" },
    { value: "TR", label: "Time Range" },
  ];

  // US States options with state codes
  const stateOptions = [
    { name: "Alabama", code: "AL" },
    { name: "Alaska", code: "AK" },
    { name: "Arizona", code: "AZ" },
    { name: "Arkansas", code: "AR" },
    { name: "California", code: "CA" },
    { name: "Colorado", code: "CO" },
    { name: "Connecticut", code: "CT" },
    { name: "Delaware", code: "DE" },
    { name: "Florida", code: "FL" },
    { name: "Georgia", code: "GA" },
    { name: "Hawaii", code: "HI" },
    { name: "Idaho", code: "ID" },
    { name: "Illinois", code: "IL" },
    { name: "Indiana", code: "IN" },
    { name: "Iowa", code: "IA" },
    { name: "Kansas", code: "KS" },
    { name: "Kentucky", code: "KY" },
    { name: "Louisiana", code: "LA" },
    { name: "Maine", code: "ME" },
    { name: "Maryland", code: "MD" },
    { name: "Massachusetts", code: "MA" },
    { name: "Michigan", code: "MI" },
    { name: "Minnesota", code: "MN" },
    { name: "Mississippi", code: "MS" },
    { name: "Missouri", code: "MO" },
    { name: "Montana", code: "MT" },
    { name: "Nebraska", code: "NE" },
    { name: "Nevada", code: "NV" },
    { name: "New Hampshire", code: "NH" },
    { name: "New Jersey", code: "NJ" },
    { name: "New Mexico", code: "NM" },
    { name: "New York", code: "NY" },
    { name: "North Carolina", code: "NC" },
    { name: "North Dakota", code: "ND" },
    { name: "Ohio", code: "OH" },
    { name: "Oklahoma", code: "OK" },
    { name: "Oregon", code: "OR" },
    { name: "Pennsylvania", code: "PA" },
    { name: "Rhode Island", code: "RI" },
    { name: "South Carolina", code: "SC" },
    { name: "South Dakota", code: "SD" },
    { name: "Tennessee", code: "TN" },
    { name: "Texas", code: "TX" },
    { name: "Utah", code: "UT" },
    { name: "Vermont", code: "VT" },
    { name: "Virginia", code: "VA" },
    { name: "Washington", code: "WA" },
    { name: "West Virginia", code: "WV" },
    { name: "Wisconsin", code: "WI" },
    { name: "Wyoming", code: "WY" },
  ];

  // Load branches when state is selected
  useEffect(() => {
    if (selectedState) {
      const stateObj = stateOptions.find((s) => s.name === selectedState);
      if (stateObj) {
        loadBranches(stateObj.code);
      }
    } else {
      setBranches([]);
      setSelectedBranch("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedState]);

  const loadBranches = async (stateCode: string): Promise<IBranch[]> => {
    try {
      setIsLoadingBranches(true);
      const response = await jobDetailsApi.searchBranches(stateCode);
      if (response.data.status === "success" && response.data.data) {
        setBranches(response.data.data);
        if (response.data.data.length === 0) {
          // FB-34: ABC only lists branches tied to the company's sellable ship-to accounts in that state.
          notify("warning", `No ABC Supply branch available in ${stateCode}: there is no active ABC ship-to account for this state.`);
        }
        return response.data.data;
      }
      return [];
    } catch (error: any) {
      notify("error", error?.response?.data?.message || "Failed to load branches");
      setBranches([]);
      return [];
    } finally {
      setIsLoadingBranches(false);
    }
  };

  // Search products with debounce
  const searchProducts = useCallback(
    async (itemId: string, searchText: string) => {
      if (!searchText || searchText.length < 2) {
        setProductSuggestions((prev) => ({ ...prev, [itemId]: [] }));
        setShowProductDropdown((prev) => ({ ...prev, [itemId]: false }));
        return;
      }

      // Get branch number from selected branch
      const selectedBranchObj = branches.find((b) => b.branchName === selectedBranch);
      if (!selectedBranchObj) {
        return;
      }

      try {
        setIsLoadingProducts((prev) => ({ ...prev, [itemId]: true }));
        setShowProductDropdown((prev) => ({ ...prev, [itemId]: true }));
        const response = await jobDetailsApi.searchProducts(selectedBranchObj.branchNumber, searchText);
        if (response.data.status === "success" && response.data.data) {
          setProductSuggestions((prev) => ({ ...prev, [itemId]: response.data.data || [] }));
        }
      } catch (error: any) {
        notify("error", error?.response?.data?.message || "Failed to search products");
        setProductSuggestions((prev) => ({ ...prev, [itemId]: [] }));
      } finally {
        setIsLoadingProducts((prev) => ({ ...prev, [itemId]: false }));
      }
    },
    [selectedBranch, branches]
  );

  // Debounce search products
  const debouncedSearchProducts = useRef<{ [key: string]: NodeJS.Timeout }>({});
  // Debounce pricing requests
  const debouncedPricingRequests = useRef<{ [key: string]: NodeJS.Timeout }>({});

  const handleItemNameChange = (sectionId: string, itemId: string, value: string) => {
    handleItemChange(sectionId, itemId, "itemName", value);

    // Clear previous timeout
    if (debouncedSearchProducts.current[itemId]) {
      clearTimeout(debouncedSearchProducts.current[itemId]);
    }

    // Set new timeout for search
    debouncedSearchProducts.current[itemId] = setTimeout(() => {
      searchProducts(itemId, value);
    }, 500);
  };

  // Fetch pricing for an item
  const fetchItemPricing = async (sectionId: string, itemId: string, item: OrderItem) => {
    // Check if we have all required data
    if (!item.itemNumber || !item.qty || item.qty <= 0) {
      return;
    }

    // Get branch information
    const selectedBranchObj = branches.find((b) => b.branchName === selectedBranch);
    if (!selectedBranchObj) {
      return;
    }

    // Build product object from item
    const product: IProduct = {
      itemNumber: item.itemNumber,
      itemDescription: item.itemDescription || "",
      familyName: item.familyName || null,
      familyId: item.familyId || null,
      isDimensional: item.isDimensional || false,
      colorName: item.colorName || "",
      category: item.category || "",
      status: item.status || "",
      variationUom: item.variationUom || "",
      variationUomCode: item.variationUomCode || "",
      uom: item.uom,
    };

    const payload: IProductPricingPayload = {
      shipToAccount: selectedBranchObj.shipToAccount,
      branchNumber: selectedBranchObj.branchNumber,
      purpose: "ordering",
      lines: [
        {
          product,
          quantity: item.qty,
        },
      ],
    };

    try {
      setIsLoadingPricing((prev) => ({ ...prev, [itemId]: true }));
      const response = await jobDetailsApi.getProductPricing(payload);
      if (response.data.status === "success" && response.data.data && response.data.data.length > 0) {
        const pricingData = response.data.data[0];
        setSections((prevSections) =>
          prevSections.map((section) => {
            if (section.id === sectionId) {
              return {
                ...section,
                items: section.items.map((i) => {
                  if (i.id === itemId) {
                    return {
                      ...i,
                      price: pricingData.price,
                      lineTotal: pricingData.lineTotal,
                    };
                  }
                  return i;
                }),
              };
            }
            return section;
          })
        );
      }
    } catch (error: any) {
      notify("error", error?.response?.data?.message || "Failed to fetch pricing");
    } finally {
      setIsLoadingPricing((prev) => ({ ...prev, [itemId]: false }));
    }
  };

  const handleSelectProduct = async (sectionId: string, itemId: string, product: IProduct) => {
    setSections((prevSections) =>
      prevSections.map((section) => {
        if (section.id === sectionId) {
          return {
            ...section,
            items: section.items.map((item) => {
              if (item.id === itemId) {
                const updatedItem = {
                  ...item,
                  itemName: product.itemNumber + " - " + product.itemDescription,
                  itemNumber: product.itemNumber,
                  itemDescription: product.itemDescription,
                  familyName: product.familyName,
                  familyId: product.familyId,
                  isDimensional: product.isDimensional,
                  colorName: product.colorName,
                  category: product.category,
                  status: product.status,
                  variationUom: product.variationUom,
                  variationUomCode: product.variationUomCode,
                  uom: product.uom,
                  qty: item.qty === 0 ? 1 : item.qty, // Auto-set qty to 1 if currently 0
                };
                // Fetch pricing if quantity > 0
                if (updatedItem.qty > 0) {
                  setTimeout(() => {
                    fetchItemPricing(sectionId, itemId, updatedItem);
                  }, 100);
                }
                return updatedItem;
              }
              return item;
            }),
          };
        }
        return section;
      })
    );
    setShowProductDropdown((prev) => ({ ...prev, [itemId]: false }));
    setProductSuggestions((prev) => ({ ...prev, [itemId]: [] }));
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showStateDropdown && stateRef.current && !stateRef.current.contains(event.target as Node)) {
        setShowStateDropdown(false);
      }
      if (showBranchDropdown && branchRef.current && !branchRef.current.contains(event.target as Node)) {
        setShowBranchDropdown(false);
      }
      // Close product dropdowns
      Object.keys(showProductDropdown).forEach((itemId) => {
        if (showProductDropdown[itemId] && productRefs.current[itemId] && !productRefs.current[itemId]?.contains(event.target as Node)) {
          setShowProductDropdown((prev) => ({ ...prev, [itemId]: false }));
        }
      });
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showStateDropdown, showBranchDropdown, showProductDropdown]);

  // Parse ISO date to date and time
  const parseDateTime = (isoString: string): { date: string; time: string } => {
    try {
      const date = new Date(isoString);
      const dateStr = date.toISOString().split("T")[0];
      const timeStr = date.toTimeString().split(" ")[0].slice(0, 5);
      return { date: dateStr, time: timeStr };
    } catch {
      return { date: "", time: "" };
    }
  };

  // Parse ISO date to date only (for requestedDeliveryDate)
  const parseDate = (isoString: string): string => {
    try {
      const date = new Date(isoString);
      return date.toISOString().split("T")[0];
    } catch {
      return "";
    }
  };

  // Common function to load and fill order data (used for both clone and edit)
  const loadAndFillOrderData = async (orderId: string, isEdit: boolean = false) => {
    try {
      setIsLoadingOrderData(true);
      if (!isEdit) {
        setIsCloning(true);
      }

      // Call API to get material order detail using id
      const response = await jobDetailsApi.getMaterialOrderDetail(jobId, orderId);

      if (response.data.status === "success" && response.data.data) {
        const orderDetail = response.data.data;

        // Store current order status for validation
        setCurrentOrderStatus(orderDetail.status);

        // Parse date to orderDate and orderTime
        const { date: orderDateValue, time: orderTimeValue } = parseDateTime(orderDetail.date);

        // Set basic fields
        setMaterialOrderId(orderDetail.materialOrderId);
        setOrderDate(orderDateValue);
        setOrderTime(orderTimeValue);

        // Set delivery address fields
        setStreetAddress(orderDetail.streetAddress || "");
        setCity(orderDetail.city || "");
        setState(orderDetail.state || "");
        setZipCode(orderDetail.zipCode || "");
        setRequestedDeliveryDate(parseDate(orderDetail.requestedDeliveryDate || ""));
        setNotes(orderDetail.notes || orderDetail.internalNotes || "");
        setNotesToSupplier(orderDetail.notesToSupplier || "");

        // Set time fields
        setFromTime(orderDetail.fromTime || "");
        setToTime(orderDetail.toTime || "");

        // Set delivery service and instructions type code
        if (orderDetail.deliveryService) {
          setDeliveryService(orderDetail.deliveryService);
        }
        if (orderDetail.instructionsTypeCode) {
          setInstructionsTypeCode(orderDetail.instructionsTypeCode);
        }

        // Find and set state and branch using stateBranch
        const stateCode = orderDetail.stateBranch || "MA"; // Default to "MA" if null
        const stateObj = stateOptions.find((s) => s.code === stateCode);

        if (stateObj) {
          // Set state
          setSelectedState(stateObj.name);

          // Load branches for this state and get the result
          const loadedBranches = await loadBranches(stateObj.code);

          // Find and set branch from loaded branches
          if (orderDetail.branchNumber && loadedBranches.length > 0) {
            const branch = loadedBranches.find(
              (b) => b.branchNumber === orderDetail.branchNumber.toString()
            );

            if (branch) {
              setSelectedBranch(branch.branchName);
            } else {
              notify("info", `Branch ${orderDetail.branchNumber} not found in state ${stateObj.name}.`);
            }
          }
        } else if (orderDetail.branchNumber) {
          // If state not found but have branch number, try to find in current branches
          const branch = branches.find(
            (b) => b.branchNumber === orderDetail.branchNumber.toString()
          );
          if (branch) {
            setSelectedBranch(branch.branchName);
          } else {
            notify("info", `Branch ${orderDetail.branchNumber} found. Please select the state to load branches.`);
          }
        }

        // Map sections and items
        if (orderDetail.sections && orderDetail.sections.length > 0) {
          const mappedSections: Section[] = orderDetail.sections.map((section, sectionIndex) => {
            const sectionId = `section-${Date.now()}-${sectionIndex}`;

            // Map lines to items
            const mappedItems: OrderItem[] = section.lines.map((line, itemIndex) => {
              const itemId = `${sectionId}-${itemIndex}`;
              const product = line.product;

              return {
                id: itemId,
                itemName: `${product.itemNumber} - ${product.itemDescription}`,
                itemNumber: product.itemNumber,
                itemDescription: product.itemDescription || "",
                familyName: product.familyName || null,
                familyId: product.familyId || null,
                isDimensional: product.isDimensional || false,
                colorName: product.colorName || "",
                category: product.category || "",
                status: product.status || "",
                variationUom: product.variationUom || "",
                variationUomCode: product.variationUomCode || "",
                uom: product.uom,
                qty: parseFloat(line.quantity) || 0,
                price: parseFloat(line.price) || 0,
                lineTotal: line.lineTotal || 0,
              };
            });

            return {
              id: sectionId,
              name: `Section ${sectionIndex + 1}`,
              items: mappedItems,
            };
          });

          setSections(mappedSections);
        }

        notify("success", isEdit ? "Order data loaded successfully" : "Order data cloned successfully");
      } else {
        notify("error", response.data.message || "Failed to load order details");
      }
    } catch (error: any) {
      notify("error", error?.response?.data?.message || `Failed to ${isEdit ? "load" : "clone"} order`);
    } finally {
      setIsLoadingOrderData(false);
      setIsCloning(false);
    }
  };

  const handleClone = async () => {
    if (!selectedPreviousOrder) {
      notify("warning", "Please select a previous order first");
      return;
    }

    // Find order from previousOrders to get the id
    const order = previousOrders.find((o) => o.materialOrderId === selectedPreviousOrder);
    if (!order) {
      notify("error", "Selected order not found");
      return;
    }

    // Use common function to load and fill data
    await loadAndFillOrderData(order.id, false);
  };

  // Load order data when editing
  useEffect(() => {
    if (editOrderId) {
      loadAndFillOrderData(editOrderId, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editOrderId]);

  const handleItemChange = (sectionId: string, itemId: string, field: keyof OrderItem, value: string | number) => {
    setSections((prevSections) =>
      prevSections.map((section) => {
        if (section.id === sectionId) {
          return {
            ...section,
            items: section.items.map((item) => {
              if (item.id === itemId) {
                const updatedItem = { ...item, [field]: value };
                // Fetch pricing when quantity changes and product is selected
                if (field === "qty" && updatedItem.itemNumber) {
                  // Clear previous timeout
                  if (debouncedPricingRequests.current[itemId]) {
                    clearTimeout(debouncedPricingRequests.current[itemId]);
                  }
                  // Set new timeout for pricing request
                  debouncedPricingRequests.current[itemId] = setTimeout(() => {
                    fetchItemPricing(sectionId, itemId, updatedItem);
                  }, 500);
                }
                return updatedItem;
              }
              return item;
            }),
          };
        }
        return section;
      })
    );
  };

  const handleAddItem = (sectionId: string) => {
    setSections((prevSections) =>
      prevSections.map((section) => {
        if (section.id === sectionId) {
          const newItem: OrderItem = {
            id: `${sectionId}-${Date.now()}`,
            itemNumber: "",
            itemName: "",
            uom: "Unit",
            qty: 0,
            price: 0,
            lineTotal: 0,
          };
          return {
            ...section,
            items: [...section.items, newItem],
          };
        }
        return section;
      })
    );
  };

  const handleDeleteItem = (sectionId: string, itemId: string) => {
    setSections((prevSections) =>
      prevSections.map((section) => {
        if (section.id === sectionId) {
          if (section.items.length <= 1) {
            notify("warning", "At least one item is required");
            return section;
          }
          return {
            ...section,
            items: section.items.filter((item) => item.id !== itemId),
          };
        }
        return section;
      })
    );
  };

  const handleIncrementQty = (sectionId: string, itemId: string) => {
    const section = sections.find((s) => s.id === sectionId);
    if (section) {
      const item = section.items.find((i) => i.id === itemId);
      if (item) {
        const newQty = item.qty + 1;
        handleItemChange(sectionId, itemId, "qty", newQty);
        // Pricing will be fetched automatically via handleItemChange debounce
      }
    }
  };

  const handleDecrementQty = (sectionId: string, itemId: string) => {
    const section = sections.find((s) => s.id === sectionId);
    if (section) {
      const item = section.items.find((i) => i.id === itemId);
      if (item && item.qty > 0) {
        const newQty = item.qty - 1;
        handleItemChange(sectionId, itemId, "qty", newQty);
        // Pricing will be fetched automatically via handleItemChange debounce
      }
    }
  };

  const handleAddSection = () => {
    const newSectionNumber = sections.length + 1;
    const newSection: Section = {
      id: Date.now().toString(),
      name: `Section ${newSectionNumber}`,
      items: [
        { id: `${Date.now()}-1`, itemName: "", itemNumber: "", uom: "Unit", qty: 0, price: 0, lineTotal: 0 },
      ],
    };
    setSections((prev) => [...prev, newSection]);
  };

  const calculateTotalItems = (): number => {
    return sections.reduce((total, section) => total + section.items.length, 0);
  };

  const calculateTotalQuantity = (): number => {
    return sections.reduce((total, section) => {
      return total + section.items.reduce((sectionTotal, item) => sectionTotal + item.qty, 0);
    }, 0);
  };

  const calculateTotalCost = (): number => {
    return sections.reduce((total, section) => {
      return total + section.items.reduce((sectionTotal, item) => sectionTotal + item.lineTotal, 0);
    }, 0);
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // Format date and time to ISO format
  const formatDateTime = (date: string, time: string): string => {
    if (!date || !time) return "";
    // Use the date string directly to avoid timezone conversion
    return `${date}T${time}:00.000Z`;
  };

  // Format date to ISO format (for requestedDeliveryDate)
  const formatDateToISO = (date: string): string => {
    if (!date) return "";
    // Use the date string directly to avoid timezone conversion
    return `${date}T00:00:00.000Z`;
  };

  // Build payload helper function
  const buildPayload = (): ICreateMaterialOrderPayload | null => {
    // Get branch information
    const selectedBranchObj = branches.find((b) => b.branchName === selectedBranch);
    if (!selectedBranchObj) {
      notify("error", "Selected branch not found");
      return null;
    }

    // Build sections with lines
    const sectionsData = sections
      .filter((section) => section.items.some((item) => item.itemNumber && item.qty > 0))
      .map((section) => ({
        sectionName: section.name,
        lines: section.items
          .filter((item) => item.itemNumber && item.qty > 0)
          .map((item) => ({
            product: {
              itemNumber: item.itemNumber,
              itemDescription: item.itemDescription || "",
              familyName: item.familyName || null,
              familyId: item.familyId || null,
              isDimensional: item.isDimensional || false,
              colorName: item.colorName || "",
              category: item.category || "",
              status: item.status || "",
              variationUom: item.variationUom || "",
              variationUomCode: item.variationUomCode || "",
              uom: item.uom,
            },
            quantity: item.qty,
            price: item.price,
          })),
      }))
      .filter((section) => section.lines.length > 0);

    // Get state code from selectedState
    const stateObj = stateOptions.find((s) => s.name === selectedState);
    const stateBranch = stateObj ? stateObj.code : "";

    // Build payload
    const payload: ICreateMaterialOrderPayload = {
      materialOrderId: materialOrderId || "",
      date: orderDate && orderTime ? formatDateTime(orderDate, orderTime) : "",
      streetAddress: streetAddress || "",
      city: city || "",
      state: state || "",
      stateBranch,
      zipCode: zipCode || "",
      requestedDeliveryDate: requestedDeliveryDate ? formatDateToISO(requestedDeliveryDate) : "",
      notes: notes || "",
      notesToSupplier: notesToSupplier || "",
      shipToAccount: selectedBranchObj.shipToAccount,
      deliveryService: deliveryService || "",
      instructionsTypeCode: instructionsTypeCode || "",
      fromTime: fromTime || "",
      toTime: toTime || "",
      branchNumber: selectedBranchObj.branchNumber,
      sections: sectionsData,
    };

    return payload;
  };

  const handleSaveDraft = async () => {
    // Minimal validation for draft - only check if branch is selected
    if (!selectedBranch) {
      notify("warning", "Please select a branch");
      return;
    }

    const payload = buildPayload();
    if (!payload) return;

    try {
      setIsSavingDraft(true);

      if (editOrderId) {
        // Update existing draft
        const response = await jobDetailsApi.updateMaterialOrderDraft(jobId, editOrderId, payload);

        if (response.data.status === "success") {
          notify("success", "Draft saved successfully");
          onSubmit(); // Call callback to refresh list
        } else {
          notify("error", response.data.message || "Failed to save draft");
        }
      } else {
        // Create new draft
        const response = await jobDetailsApi.createMaterialOrderDraft(jobId, payload);

        if (response.data.status === "success") {
          notify("success", "Draft saved successfully");
          onSubmit(); // Call callback to refresh list
        } else {
          notify("error", response.data.message || "Failed to save draft");
        }
      }
    } catch (error: any) {
      notify("error", error?.response?.data?.message || "Failed to save draft");
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleSubmit = async () => {
    // Clear previous errors
    setErrors({});

    // Validation
    const newErrors: typeof errors = {};

    if (!selectedBranch) newErrors.selectedBranch = "Branch is required";
    if (!materialOrderId) newErrors.materialOrderId = "Material Order ID is required";
    if (!orderDate) newErrors.orderDate = "Order Date is required";
    if (!orderTime) newErrors.orderTime = "Order Time is required";
    if (!streetAddress) newErrors.streetAddress = "Street Address is required";
    if (!city) newErrors.city = "City is required";
    if (!state) newErrors.state = "State is required";
    if (!zipCode) newErrors.zipCode = "ZIP Code is required";
    if (!deliveryService) newErrors.deliveryService = "Delivery Service is required";
    if (!instructionsTypeCode) newErrors.instructionsTypeCode = "Instructions Type Code is required";

    // Validate sections and items
    const hasValidItems = sections.some((section) =>
      section.items.some((item) => item.itemNumber && item.qty > 0)
    );

    if (!hasValidItems) {
      notify("warning", "Please add at least one item with quantity > 0");
      return;
    }

    // If there are errors, set them and return
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const payload = buildPayload();
    if (!payload) {
      setIsSubmitting(false);
      return;
    }

    try {
      setIsSubmitting(true);

      // Check if editing a draft
      if (editOrderId) {
        // Check if order is a draft
        if (currentOrderStatus !== "Draft") {
          notify("error", "Cannot edit submitted order. Only draft orders can be edited.");
          setIsSubmitting(false);
          return;
        }

        // Submit draft: Update with PUT
        const response = await jobDetailsApi.updateMaterialOrder(jobId, editOrderId, payload);

        if (response.data.status === "success") {
          notify("success", "Material order submitted successfully");
          onSubmit(); // Call callback to refresh list
        } else {
          notify("error", response.data.message || "Failed to submit material order");
        }
      } else {
        // Submit new order: Create with POST
        const response = await jobDetailsApi.createMaterialOrder(jobId, payload);

        if (response.data.status === "success") {
          notify("success", "Material order created successfully");
          onSubmit(); // Call callback to refresh list
        } else {
          notify("error", response.data.message || "Failed to create material order");
        }
      }
    } catch (error: any) {
      notify("error", error?.response?.data?.message || "Failed to create material order");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full h-full flex flex-col bg-[#071427] text-white relative">
      {/* Loading Overlay */}
      {isLoadingOrderData && (
        <div className="absolute inset-0 bg-[#071427] bg-opacity-90 z-50 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-[#3b82f6] border-t-transparent rounded-full animate-spin"></div>
            <p className="text-white text-lg">
              {editOrderId ? "Loading order data..." : "Cloning order..."}
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-4 p-4 border-b border-[#111e33]">
        {/* <button
          onClick={onCancel}
          className="p-2 hover:bg-[#1f2937] rounded transition-colors"
        >
          <ArrowLeftIcon className="w-5 h-5 text-white" />
        </button> */}
        <h2 className="text-xl font-semibold text-white">
          {editOrderId ? "Edit Material Order" : "Create Material Order"}
        </h2>
      </div>

      {/* Form */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-4 gap-6">
            {/* State */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-white">State</label>
              <div className="relative" ref={stateRef}>
                <button
                  onClick={() => setShowStateDropdown(!showStateDropdown)}
                  className="w-full px-4 py-2 bg-[#030E1F] border border-[#1f2937] text-white rounded-lg text-left flex items-center justify-between hover:bg-[#0a1b34] transition-colors"
                >
                  <span className={selectedState ? "text-white" : "text-[#A2A7B4]"}>
                    {selectedState || "Select State"}
                  </span>
                  <ArrowDownWhiteIcon className="w-4 h-4" />
                </button>
                {showStateDropdown && (
                  <div className="absolute top-full mt-2 w-full z-20 bg-[#0C192D] border border-[#1f2937] rounded-lg shadow-lg overflow-hidden max-h-60 overflow-y-auto">
                    {stateOptions.map((option) => (
                      <button
                        key={option.code}
                        onClick={() => {
                          setSelectedState(option.name);
                          setShowStateDropdown(false);
                        }}
                        className={`w-full text-left px-4 py-2 hover:bg-[#1f2937] transition-colors ${selectedState === option.name
                          ? "bg-[#3b82f6] text-white"
                          : "text-white"
                          }`}
                      >
                        {option.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Select Branch */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-white">Select Branch</label>
              <div className="relative" ref={branchRef}>
                <button
                  onClick={() => {
                    if (!selectedState) {
                      notify("warning", "Please select a state first");
                      return;
                    }
                    setShowBranchDropdown(!showBranchDropdown);
                  }}
                  disabled={!selectedState || isLoadingBranches}
                  className={`w-full px-4 py-2 bg-[#030E1F] rounded-lg text-left flex items-center justify-between hover:bg-[#0a1b34] transition-colors ${!selectedState || isLoadingBranches ? "opacity-50 cursor-not-allowed" : ""
                    } ${errors.selectedBranch ? "border border-red-500" : "border border-[#1f2937]"
                    } text-white`}
                >
                  <span
                    className={`${selectedBranch ? "text-white" : "text-[#A2A7B4]"} truncate flex-1 text-left`}
                    title={selectedBranch || undefined}
                  >
                    {isLoadingBranches
                      ? "Loading branches..."
                      : selectedBranch || "Select Branch"}
                  </span>
                  <ArrowDownWhiteIcon className="w-4 h-4 flex-shrink-0 ml-2" />
                </button>
                {errors.selectedBranch && (
                  <p className="text-xs text-red-500 mt-1">{errors.selectedBranch}</p>
                )}
                {showBranchDropdown && branches.length > 0 && (
                  <div className="absolute top-full mt-2 w-full z-20 bg-[#0C192D] border border-[#1f2937] rounded-lg shadow-lg overflow-hidden max-h-60 overflow-y-auto">
                    {branches.map((branch) => (
                      <button
                        key={branch.branchNumber}
                        onClick={() => {
                          setSelectedBranch(branch.branchName);
                          setShowBranchDropdown(false);
                          setErrors((prev) => ({ ...prev, selectedBranch: undefined }));
                        }}
                        className={`w-full text-left px-4 py-2 hover:bg-[#1f2937] transition-colors ${selectedBranch === branch.branchName
                          ? "bg-[#3b82f6] text-white"
                          : "text-white"
                          }`}
                      >
                        {branch.branchName}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Material Order ID */}
            <div className="flex flex-col gap-2">
              <PrimaryInput
                label="Material Order ID"
                name="materialOrderId"
                value={materialOrderId}
                onChange={(e) => {
                  setMaterialOrderId(e.target.value);
                  setErrors((prev) => ({ ...prev, materialOrderId: undefined }));
                }}
                placeholder="Enter Material Order ID"
                inputStyle={{ backgroundColor: '#030E1F', border: errors.materialOrderId ? '1px solid #ef4444' : '1px solid #1f2937' }}
                error={!!errors.materialOrderId}
                errorMessage={errors.materialOrderId}
              />
            </div>

            {/* Order Date */}
            <div className="flex flex-col gap-2">
              <PrimaryInput
                label="Order Date"
                name="orderDate"
                type="date"
                value={orderDate}
                onChange={(e) => {
                  setOrderDate(e.target.value);
                  setErrors((prev) => ({ ...prev, orderDate: undefined }));
                }}
                placeholder="Order Date"
                inputStyle={{ backgroundColor: '#030E1F', border: errors.orderDate ? '1px solid #ef4444' : '1px solid #1f2937' }}
                error={!!errors.orderDate}
                errorMessage={errors.orderDate}
              />
            </div>

          </div>

          {/* Second Row - Order Time and Copy from Previous Order */}
          <div className="grid grid-cols-2 gap-6 mt-6">
            {/* Order Time */}
            <div className="flex flex-col gap-2">
              <PrimaryInput
                label="Order Time"
                name="orderTime"
                type="time"
                value={orderTime ? new Date(`2000-01-01T${orderTime}:00`).toISOString() : ""}
                onChange={(e) => {
                  if (e.target.value) {
                    const date = new Date(e.target.value);
                    const timeStr = `${date.getHours().toString().padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}`;
                    setOrderTime(timeStr);
                  } else {
                    setOrderTime("");
                  }
                  setErrors((prev) => ({ ...prev, orderTime: undefined }));
                }}
                inputStyle={{ backgroundColor: '#030E1F', border: errors.orderTime ? '1px solid #ef4444' : '1px solid #1f2937' }}
                showCalendarIcon={false}
                error={!!errors.orderTime}
                errorMessage={errors.orderTime}
              />
            </div>

            {/* Copy from Previous Order */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-white">Copy from Previous Order</label>
              <div className="flex items-center gap-3">
                <Dropdown
                  value={selectedPreviousOrder}
                  options={previousOrderOptions}
                  placeholder="Select Previous Order"
                  onChange={(value) => setSelectedPreviousOrder(value)}
                  className="flex-1"
                />
                <PrimaryButton
                  title={isCloning ? "Cloning..." : "Clone"}
                  onClick={handleClone}
                  disabled={isCloning || !selectedPreviousOrder}
                  className="!bg-[#3b82f6] !text-white hover:!bg-[#2563eb] !rounded-lg !px-6 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
            </div>
          </div>

          {/* Delivery Service, Instructions Type Code, From Time and To Time */}
          <div className="mt-8">
            <div className="space-y-6">
              {/* Delivery Service and Instructions Type Code */}
              <div className="grid grid-cols-2 gap-6">
                <Dropdown
                  label="Delivery Service"
                  value={deliveryService}
                  options={deliveryServiceOptions}
                  placeholder="Select Delivery Service"
                  error={errors.deliveryService}
                  onChange={(value) => {
                    setDeliveryService(value);
                    setErrors((prev) => ({ ...prev, deliveryService: undefined }));
                  }}
                />
                <Dropdown
                  label="Instructions Type Code"
                  value={instructionsTypeCode}
                  options={instructionsTypeCodeOptions}
                  placeholder="Select Instructions Type Code"
                  error={errors.instructionsTypeCode}
                  onChange={(value) => {
                    setInstructionsTypeCode(value);
                    setErrors((prev) => ({ ...prev, instructionsTypeCode: undefined }));
                  }}
                />
              </div>


              {/* Delivery Appointment - From Time and To Time */}
              <div className="grid grid-cols-2 gap-6">
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium text-white">Delivery Appointment</label>
                  <div className="flex items-center gap-4 bg-[#030E1F] border border-[#1f2937] rounded-lg px-4 py-2">
                    {/* From Time */}
                    <div className="flex-1">
                      <input
                        type="time"
                        value={fromTime}
                        onChange={(e) => setFromTime(e.target.value)}
                        placeholder="Start Time"
                        className="w-full bg-transparent text-white placeholder-[#5D5F61] focus:outline-none border-none [&::-webkit-calendar-picker-indicator]:filter [&::-webkit-calendar-picker-indicator]:invert"
                      />
                    </div>

                    {/* Arrow Icon */}
                    <div className="flex-shrink-0 w-4 h-4 flex items-center justify-center">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 8H15M15 8L8 1M15 8L8 15" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>

                    {/* To Time */}
                    <div className="flex-1 flex items-center">
                      <input
                        type="time"
                        value={toTime}
                        onChange={(e) => setToTime(e.target.value)}
                        placeholder="End Time"
                        className="flex-1 bg-transparent text-white placeholder-[#5D5F61] focus:outline-none border-none [&::-webkit-calendar-picker-indicator]:filter [&::-webkit-calendar-picker-indicator]:invert"
                      />
                    </div>
                  </div>
                </div>
                <div></div>
              </div>
            </div>
          </div>

          {/* Sections */}
          <div className="mt-8 space-y-8">
            {sections.map((section, sectionIndex) => (
              <div key={section.id}>
                <h3 className="text-lg font-semibold text-white mb-4">{section.name}</h3>

                {/* Table Header */}
                <div className="bg-[#111E33] rounded-t-lg overflow-hidden">
                  <div className="grid gap-4 px-4 py-3 border-b border-[#111e33]" style={{ gridTemplateColumns: '3fr 1fr 1fr 1fr 1fr 1fr' }}>
                    <div className="text-sm font-semibold text-white">Item Name</div>
                    <div className="text-sm font-semibold text-white">UoM</div>
                    <div className="text-sm font-semibold text-white">Qty</div>
                    <div className="text-sm font-semibold text-white">Price</div>
                    <div className="text-sm font-semibold text-white">Line Total</div>
                    <div className="text-sm font-semibold text-white">Action</div>
                  </div>
                </div>

                {/* Table Body */}
                <div className="bg-[#030E1F] rounded-b-lg overflow-visible">
                  {section.items.map((item, index) => (
                    <div
                      key={item.id}
                      className={`grid gap-4 px-4 py-3 ${index !== section.items.length - 1 ? "border-b border-[#111e33]" : ""
                        }`}
                      style={{ gridTemplateColumns: '3fr 1fr 1fr 1fr 1fr 1fr' }}
                    >
                      {/* Item Name */}
                      <div className="relative" ref={(el) => (productRefs.current[item.id] = el)}>
                        <input
                          type="text"
                          value={item.itemName}
                          onChange={(e) => handleItemNameChange(section.id, item.id, e.target.value)}
                          onFocus={() => {
                            if (productSuggestions[item.id] && productSuggestions[item.id].length > 0) {
                              setShowProductDropdown((prev) => ({ ...prev, [item.id]: true }));
                            }
                          }}
                          placeholder="Enter item name"
                          className="w-full px-3 py-2 bg-[#030E1F] border border-[#1f2937] rounded-lg text-white placeholder-[#A2A7B4] text-sm focus:outline-none focus:border-[#3b82f6]"
                        />
                        {showProductDropdown[item.id] && (
                          <div className="absolute top-full mt-1 w-full z-50 bg-[#0C192D] border border-[#1f2937] rounded-lg shadow-lg max-h-60 overflow-y-auto">
                            {isLoadingProducts[item.id] ? (
                              <div className="px-4 py-2 text-sm text-[#A2A7B4]">Loading...</div>
                            ) : productSuggestions[item.id] && productSuggestions[item.id].length > 0 ? (
                              productSuggestions[item.id].map((product, idx) => (
                                <button
                                  key={`${product.itemNumber}-${product.uom}-${idx}`}
                                  onClick={() => handleSelectProduct(section.id, item.id, product)}
                                  className="w-full text-left px-4 py-2 hover:bg-[#1f2937] transition-colors text-white text-sm border-b border-[#1f2937] last:border-b-0"
                                >
                                  <div className="font-medium">{product.itemDescription}</div>
                                  <div className="text-xs text-[#A2A7B4] mt-1">
                                    {product.itemNumber} • {product.uom}
                                  </div>
                                </button>
                              ))
                            ) : (
                              <div className="px-4 py-2 text-sm text-[#A2A7B4]">No products found</div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* UoM */}
                      <div>
                        <input
                          type="text"
                          value={item.uom}
                          onChange={(e) => handleItemChange(section.id, item.id, "uom", e.target.value)}
                          className="w-full px-3 py-2 bg-[#030E1F] border border-[#1f2937] rounded-lg text-white text-sm focus:outline-none focus:border-[#3b82f6]"
                        />
                      </div>

                      {/* Qty */}
                      <div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleDecrementQty(section.id, item.id)}
                            className="px-2 py-1 text-white hover:bg-[#1f2937] rounded transition-colors"
                          >
                            -
                          </button>
                          <input
                            type="number"
                            value={item.qty}
                            onChange={(e) => handleItemChange(section.id, item.id, "qty", parseFloat(e.target.value) || 0)}
                            min="0"
                            className="w-16 px-2 py-2 bg-[#030E1F] border border-[#1f2937] rounded-lg text-white text-sm text-center focus:outline-none focus:border-[#3b82f6] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                          />
                          <button
                            onClick={() => handleIncrementQty(section.id, item.id)}
                            className="px-2 py-1 text-white hover:bg-[#1f2937] rounded transition-colors"
                          >
                            +
                          </button>
                        </div>
                      </div>

                      {/* Price */}
                      <div className="relative">
                        <input
                          type="number"
                          value={item.price || ""}
                          readOnly
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                          className="w-full px-3 py-2 bg-[#030E1F] border border-[#1f2937] rounded-lg text-white placeholder-[#A2A7B4] text-sm focus:outline-none cursor-not-allowed"
                        />
                        {isLoadingPricing[item.id] && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <div className="w-4 h-4 border-2 border-[#3b82f6] border-t-transparent rounded-full animate-spin"></div>
                          </div>
                        )}
                      </div>

                      {/* Line Total */}
                      <div className="flex items-center">
                        <span className="text-sm text-white">{formatCurrency(item.lineTotal)}</span>
                      </div>

                      {/* Action */}
                      <div className="flex items-center">
                        <button
                          onClick={() => handleDeleteItem(section.id, item.id)}
                          className="text-sm text-red-500 hover:text-red-400 transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}

                  {/* Add Item Button - Inside table */}
                  <div className="grid gap-4 px-4 py-3 border-t border-[#111e33]" style={{ gridTemplateColumns: '3fr 1fr 1fr 1fr 1fr 1fr' }}>
                    <div className="col-span-6">
                      <button
                        onClick={() => handleAddItem(section.id)}
                        className="flex items-center gap-2 text-[#3b82f6] hover:text-[#2563eb] transition-colors"
                      >
                        <PlusIcon className="w-2 h-2 [&>path]:stroke-[#2D80FC]" />
                        <span className="text-sm font-medium">Add Item</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}

          </div>

          {/* Add Section Button and Total Cost - Same Row */}
          <div className="mt-8 flex items-center justify-between">
            {/* Add Section Button */}
            <button
              onClick={handleAddSection}
              className="flex items-center gap-2 text-[#3b82f6] hover:text-[#2563eb] transition-colors self-start"
            >
              <PlusIcon className="w-2 h-2 [&>path]:stroke-[#2D80FC]" />
              <span className="text-sm font-medium">Add Section</span>
            </button>

            {/* Total Cost Card */}
            <div className="bg-[#030E1F] rounded-lg px-6 py-4 min-w-[200px]">
              <div className="text-sm text-white mb-2">Total Cost:</div>
              <div className="text-2xl font-bold text-[#3b82f6]">{formatCurrency(calculateTotalCost())}</div>
            </div>
          </div>

          {/* Delivery Address Form */}
          <div className="mt-8">
            <h3 className="text-lg font-semibold text-white mb-6">Delivery Address</h3>

            <div className="space-y-6">
              {/* Street Address */}
              <div className="flex flex-col gap-2">
                <PrimaryInput
                  label="Street Address"
                  name="streetAddress"
                  value={streetAddress}
                  onChange={(e) => {
                    setStreetAddress(e.target.value);
                    setErrors((prev) => ({ ...prev, streetAddress: undefined }));
                  }}
                  placeholder="Enter street address"
                  inputStyle={{ backgroundColor: '#030E1F', border: errors.streetAddress ? '1px solid #ef4444' : '1px solid #1f2937' }}
                  error={!!errors.streetAddress}
                  errorMessage={errors.streetAddress}
                />
              </div>

              {/* City and State */}
              <div className="grid grid-cols-2 gap-6">
                <div className="flex flex-col gap-2">
                  <PrimaryInput
                    label="City"
                    name="city"
                    value={city}
                    onChange={(e) => {
                      setCity(e.target.value);
                      setErrors((prev) => ({ ...prev, city: undefined }));
                    }}
                    placeholder="Enter city"
                    inputStyle={{ backgroundColor: '#030E1F', border: errors.city ? '1px solid #ef4444' : '1px solid #1f2937' }}
                    error={!!errors.city}
                    errorMessage={errors.city}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <PrimaryInput
                    label="State"
                    name="state"
                    value={state}
                    onChange={(e) => {
                      setState(e.target.value);
                      setErrors((prev) => ({ ...prev, state: undefined }));
                    }}
                    placeholder="Enter state"
                    inputStyle={{ backgroundColor: '#030E1F', border: errors.state ? '1px solid #ef4444' : '1px solid #1f2937' }}
                    error={!!errors.state}
                    errorMessage={errors.state}
                  />
                </div>
              </div>

              {/* ZIP Code and Requested Delivery Date */}
              <div className="grid grid-cols-2 gap-6">
                <div className="flex flex-col gap-2">
                  <PrimaryInput
                    label="ZIP Code"
                    name="zipCode"
                    value={zipCode}
                    onChange={(e) => {
                      setZipCode(e.target.value);
                      setErrors((prev) => ({ ...prev, zipCode: undefined }));
                    }}
                    placeholder="Enter ZIP code"
                    inputStyle={{ backgroundColor: '#030E1F', border: errors.zipCode ? '1px solid #ef4444' : '1px solid #1f2937' }}
                    error={!!errors.zipCode}
                    errorMessage={errors.zipCode}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <PrimaryInput
                    label="Requested Delivery Date"
                    name="requestedDeliveryDate"
                    type="date"
                    value={requestedDeliveryDate}
                    onChange={(e) => {
                      setRequestedDeliveryDate(e.target.value);
                    }}
                    inputStyle={{ backgroundColor: '#030E1F', border: '1px solid #1f2937' }}
                  />
                </div>
              </div>

              {/* Notes and Notes to Supplier */}
              <div className="grid grid-cols-2 gap-6">
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium text-white">Notes</label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Enter notes"
                    rows={4}
                    className="w-full px-4 py-2 bg-[#030E1F] border border-[#1f2937] rounded-lg text-white placeholder-[#5D5F61] focus:outline-none focus:border-[#3b82f6] resize-none"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium text-white">Notes to Supplier</label>
                  <textarea
                    value={notesToSupplier}
                    onChange={(e) => setNotesToSupplier(e.target.value)}
                    placeholder="Enter notes to supplier"
                    rows={4}
                    className="w-full px-4 py-2 bg-[#030E1F] border border-[#1f2937] rounded-lg text-white placeholder-[#5D5F61] focus:outline-none focus:border-[#3b82f6] resize-none"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Summary Section */}
          <div className="mt-8">
            <div className="bg-[#030E1F] rounded-lg p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Summary</h3>

              <div className="space-y-3">
                {/* Total Items */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-white">Total Items:</span>
                  <span className="text-sm text-white font-medium">{calculateTotalItems()}</span>
                </div>

                {/* Total Quantity */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-white">Total Quantity:</span>
                  <span className="text-sm text-white font-medium">{calculateTotalQuantity()}</span>
                </div>

                {/* Divider */}
                <div className="border-t border-[#3b82f6] my-3"></div>

                {/* Total Cost */}
                <div className="flex items-center justify-between">
                  <span className="text-base font-semibold text-white">Total Cost:</span>
                  <span className="text-base font-semibold text-[#3b82f6]">{formatCurrency(calculateTotalCost())}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-4 mt-8">
            <PrimaryButton
              title="Cancel"
              onClick={onCancel}
              className="!bg-transparent !text-white !border !border-[#1f2937] hover:!bg-[#1f2937] !rounded-[8px] !px-6 !h-[48px]"
            />
            <PrimaryButton
              title={isSavingDraft ? "Saving..." : "Save as Draft"}
              onClick={handleSaveDraft}
              disabled={isSavingDraft || isSubmitting}
              titleStyle={{ color: '#2D80FC' }}
              className="!bg-[#030E1F] !border !border-[#2D80FC] hover:!bg-[#0a1b34] !rounded-[8px] !px-6 !h-[48px] disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <PrimaryButton
              title={isSubmitting ? "Submitting..." : "Submit Material Order"}
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="!bg-[#3b82f6] !text-white hover:!bg-[#2563eb] !rounded-[8px] !px-6 !h-[48px] disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateMaterialOrderForm;

